import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SkillConfig } from '@netsuite-ai/types';
import { apiFetch } from './api';

export interface ApiSkill {
  id: string;
  tenantId: string;
  name: string;
  description: string;
  status: 'draft' | 'published' | 'archived';
  activeVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export type { SkillConfig };

const SKILLS_KEY = ['skills'] as const;

export function useSkills() {
  return useQuery({
    queryKey: SKILLS_KEY,
    queryFn: async () => {
      const res = await apiFetch('/api/chat/skills');
      const data = await res.json();
      return data.skills as ApiSkill[];
    },
  });
}

export function useSkill(id: string) {
  return useQuery({
    queryKey: ['skills', id],
    queryFn: async () => {
      const res = await apiFetch(`/api/chat/skills/${id}`);
      return res.json() as Promise<{
        skill: ApiSkill;
        config: SkillConfig | null;
      }>;
    },
    enabled: !!id,
  });
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { name: string; description?: string }) => {
      const res = await apiFetch('/api/chat/skills', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SKILLS_KEY }),
  });
}

export function useUpdateSkill(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: {
      name?: string;
      description?: string;
      status?: string;
    }) => {
      const res = await apiFetch(`/api/chat/skills/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      });
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SKILLS_KEY }),
  });
}

export function useDeleteSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch(`/api/chat/skills/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: SKILLS_KEY }),
  });
}

export function useSaveSkillVersion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      skillId,
      config,
    }: {
      skillId: string;
      config: SkillConfig;
    }) => {
      const res = await apiFetch(`/api/chat/skills/${skillId}/versions`, {
        method: 'POST',
        body: JSON.stringify(config),
      });
      return res.json() as Promise<{
        version: { id: string; skillId: string; createdAt: string };
      }>;
    },
    onSuccess: (_, { skillId }) =>
      qc.invalidateQueries({ queryKey: ['skills', skillId] }),
  });
}

export function usePublishSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      skillId,
      versionId,
    }: {
      skillId: string;
      versionId: string;
    }) => {
      const res = await apiFetch(`/api/chat/skills/${skillId}/publish`, {
        method: 'POST',
        body: JSON.stringify({ versionId }),
      });
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: SKILLS_KEY }),
  });
}

export interface ApiTool {
  identifier: string;
  description: string;
  action: string;
  label: string;
}

export function useTools() {
  return useQuery({
    queryKey: ['tools'],
    queryFn: async () => {
      const res = await apiFetch('/api/chat/tools');
      const data = await res.json();
      return data.tools as ApiTool[];
    },
  });
}
